'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

function Tab({
  href,
  label,
  active,
  icon,
}: {
  href: string
  label: string
  active: boolean
  icon?: React.ReactNode
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      className={`flex items-center justify-center flex-1 py-0 text-[12px] leading-none font-semibold transition ${
        active
          ? 'text-ember'
          : 'text-ink-soft hover:text-ink'
      }`}
    >
      {icon ? (
        <span className="inline-flex items-center justify-center">
          {icon}
        </span>
      ) : (
        <span>{label}</span>
      )}
    </Link>
  )
}

function AnchorTab({
  href,
  label,
  active,
}: {
  href: string
  label: string
  active: boolean
}) {
  return (
    <a
      href={href}
      aria-label={label}
      className={`flex items-center justify-center flex-1 py-0 text-[12px] leading-none font-semibold ${
        active ? 'text-ember' : 'text-ink-soft hover:text-ink'
      }`}
    >
      <span>{label}</span>
    </a>
  )
}

function LogoutTab() {
  const { logout } = useAuth()
  return (
    <button
      onClick={logout}
      aria-label="Logout"
      className="flex items-center justify-center flex-1 py-0 text-[12px] leading-none font-semibold text-ink-soft/70 hover:text-ember transition"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    </button>
  )
}

export default function BottomNav() {
  const pathname = usePathname()
  const router = useRouter()
  const { user } = useAuth()
  const isUiOnly = process.env.NEXT_PUBLIC_MOBILE_UI_ONLY === 'true'

  // Scroll main content to top on route change
  useEffect(() => {
    const main = document.querySelector('.app-main')
    if (main) main.scrollTop = 0
  }, [pathname])

  const isActive = (path: string) =>
    pathname === path || pathname?.startsWith(path + '/')

  // If no driver tab is active, default to Schedule
  useEffect(() => {
    if (!user || user.type !== 'driver') return
    const onDriverTab =
      isActive('/driver/schedule') ||
      isActive('/driver/orders') ||
      isActive('/driver/routes') ||
      isActive('/driver/settings')
    if (!onDriverTab) {
      // Avoid loops: only redirect when not already at schedule
      router.replace('/driver/schedule')
    }
    // We intentionally depend on pathname to re-evaluate on route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.type, pathname, router])

  // Prefetch student tabs to avoid flicker on navigation in UI-only mode
  useEffect(() => {
    if (!isUiOnly || user?.type !== 'student') return
    router.prefetch?.('/student/orders/')
    router.prefetch?.('/student/confirm/')
    router.prefetch?.('/student/settings/')
  }, [isUiOnly, user?.type, router])

  useEffect(() => {
    if (!isUiOnly || user?.type !== 'parent') return
    router.prefetch?.('/parent/new-order/')
    router.prefetch?.('/parent/orders/')
    router.prefetch?.('/parent/settings/')
  }, [isUiOnly, user?.type, router])

  useEffect(() => {
    if (!isUiOnly || user?.type !== 'driver') return
    router.prefetch?.('/driver/schedule/')
    router.prefetch?.('/driver/orders/')
    router.prefetch?.('/driver/routes/')
    router.prefetch?.('/driver/settings/')
  }, [isUiOnly, user?.type, router])

  useEffect(() => {
    if (!isUiOnly || user) return
    router.prefetch?.('/')
    router.prefetch?.('/how-it-works/')
    router.prefetch?.('/faq/')
    router.prefetch?.('/about/')
  }, [isUiOnly, user, router])
  // UI-only mode: show static tabs and role landing when signed in
  if (isUiOnly) {
    return (
        <nav
          className="fixed bottom-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur border-t border-cream-deep"
          style={{ height: 'calc(45px + env(safe-area-inset-bottom))' }}
        >
          <div className="px-4 h-[45px] w-full">
            <div className="h-full w-full flex items-center justify-evenly">
              {user?.type ? (
                user.type === 'student' ? (
                  <>
                    <LogoutTab />
                    <Tab href="/student/orders/" label="My Orders" active={isActive('/student/orders')} />
                    <Tab href="/student/confirm/" label="Confirm" active={isActive('/student/confirm')} />
                    <Tab href="/student/settings/" label="Settings" active={isActive('/student/settings')} />
                  </>
                ) : user.type === 'parent' ? (
                  <>
                    <LogoutTab />
                    <Tab href="/parent/new-order/" label="New Order" active={isActive('/parent/new-order')} />
                    <Tab href="/parent/orders/" label="Orders" active={isActive('/parent/orders')} />
                    <Tab href="/parent/settings/" label="Settings" active={isActive('/parent/settings')} />
                  </>
                ) : user.type === 'driver' ? (
                  <>
                    <LogoutTab />
                    <Tab href="/driver/schedule/" label="Schedule" active={isActive('/driver/schedule')} />
                    <Tab href="/driver/orders/" label="Orders" active={isActive('/driver/orders')} />
                    <Tab href="/driver/routes/" label="Routes" active={isActive('/driver/routes')} />
                    <Tab href="/driver/settings/" label="Settings" active={isActive('/driver/settings')} />
                  </>
                ) : null
              ) : (
                <>
                  <Tab href="/" label="Home" active={isActive('/')} />
                  <Tab href="/how-it-works/" label="How It Works" active={isActive('/how-it-works')} />
                  <Tab href="/faq/" label="FAQ" active={isActive('/faq')} />
                  <Tab href="/about/" label="About" active={isActive('/about')} />
                </>
              )}
            </div>
          </div>
        </nav>
    )
  }

  // When signed in, replace tabs with role-specific shortcuts (was top navbar)
  if (user) {
    const { type } = user
    return (
        <nav
          className="fixed bottom-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur border-t border-cream-deep"
          style={{ height: 'calc(45px + env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto max-w-7xl px-4 h-[45px]">
            <div className="h-full flex items-center">
            {type === 'driver' && (
              <>
                <LogoutTab />
                <Tab href="/driver/schedule" label="Schedule" active={isActive('/driver/schedule')} />
                <Tab href="/driver/orders" label="Orders" active={isActive('/driver/orders')} />
                <Tab href="/driver/routes" label="Routes" active={isActive('/driver/routes')} />
                <Tab href="/driver/settings" label="Settings" active={isActive('/driver/settings')} />
                <Tab href="/test/location" label="GPS" active={isActive('/test/location')} />
              </>
            )}
            {type === 'parent' && (
              <>
                <LogoutTab />
                <Tab href="/parent/new-order" label="New Order" active={isActive('/parent/new-order')} />
                <Tab href="/parent/orders" label="Orders" active={isActive('/parent/orders')} />
                <Tab href="/parent/settings" label="Settings" active={isActive('/parent/settings')} />
              </>
            )}
            {type === 'student' && (
              <>
                <LogoutTab />
                <Tab href="/student/orders" label="Orders" active={isActive('/student/orders')} />
                <Tab href="/student/confirm" label="Confirm" active={isActive('/student/confirm')} />
                <Tab href="/student/settings" label="Settings" active={isActive('/student/settings')} />
              </>
            )}
            {type === 'admin' && (
              <>
                <LogoutTab />
                <Tab href="/admin" label="Admin" active={isActive('/admin')} />
              </>
            )}
            </div>
          </div>
        </nav>
    )
  }

  // Public tabs for signed-out users
  return (
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur border-t border-cream-deep"
        style={{ height: 'calc(45px + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto max-w-7xl px-4 h-[45px]">
          <div className="h-full flex items-center">
            <Tab
              href="/"
              label="Home"
              active={isActive('/')}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 11l9-8 9 8"></path>
                  <path d="M9 21V11h6v10"></path>
                </svg>
              }
            />
            <Tab
              href="/about"
              label="About"
              active={isActive('/about')}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              }
            />
            <Tab
              href="/how-it-works"
              label="How It Works"
              active={isActive('/how-it-works')}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 13l6-6 4 4 6-6"></path>
                  <path d="M14 7l3 3"></path>
                  <path d="M2 22l4-10 6-6 6 6-6 6-10 4z"></path>
                </svg>
              }
            />
            <Tab
              href="/faq"
              label="FAQ"
              active={isActive('/faq')}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 9a3 3 0 1 1 5.2 2 3 3 0 0 0-1.2 2.4"></path>
                  <path d="M12 17h.01"></path>
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
              }
            />
          </div>
        </div>
      </nav>
  )
}


